import type { EninCompanySearchHit, EninLookupInput, EninLookupResult } from "@repo/shared/types";
import { resolveEninCompanyId } from "@repo/shared/types";
import { useEffect, useState, useTransition } from "react";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { EninLookupPicker } from "./enin-lookup-picker";
import { EninLookupResultPanel } from "./enin-lookup-result";
import { T } from "./enin-lookup-widget.i18n";

type SelectedCompany = {
  companyId: string;
  companyName: string | null;
  /** Picked from search / re-run: POST the explicit companyId; otherwise the server re-resolves from orgId + country. */
  fromSearch: boolean;
};

type EninLookupWidgetProps = {
  clientName: string | null;
  clientCountry: string | null;
  clientOrgId: string | null;
  calculatedData?: string | null;
  onRunLookup: (input: EninLookupInput) => Promise<EninLookupResult>;
  onSearchCompanies: (name: string) => Promise<EninCompanySearchHit[]>;
  /** QP-184 — batch PEP/sanctions screening of UBOs, roles and the representative. */
  onScreenPersons?: () => Promise<EninLookupResult>;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
};

export function EninLookupWidget({
  clientName,
  clientCountry,
  clientOrgId,
  calculatedData,
  onRunLookup,
  onSearchCompanies,
  onScreenPersons,
  disabled = false,
  style = "none",
  title,
  description,
  language,
  hasValidationError = false,
}: EninLookupWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const lang = language || userLanguage;
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [screeningPending, setScreeningPending] = useState(false);
  const [screeningError, setScreeningError] = useState<string | null>(null);

  const handleScreenPersons = async () => {
    if (!onScreenPersons) return;
    setScreeningPending(true);
    setScreeningError(null);
    try {
      const updated = await onScreenPersons();
      setLocalSnapshot(updated);
    } catch (err: any) {
      setScreeningError(err?.message || "Screening failed");
    } finally {
      setScreeningPending(false);
    }
  };

  const buildSnapshot = (rawCalculated: string): EninLookupResult | null => {
    try {
      const parsed = JSON.parse(rawCalculated);
      return parsed?.hasBeenChecked ? (parsed as EninLookupResult) : null;
    } catch {
      return null;
    }
  };

  const [localSnapshot, setLocalSnapshot] = useState<EninLookupResult | null>(() =>
    calculatedData ? buildSnapshot(calculatedData) : null,
  );

  useEffect(() => {
    if (calculatedData) {
      const snapshot = buildSnapshot(calculatedData);
      if (snapshot) setLocalSnapshot(snapshot);
    }
  }, [calculatedData]);

  // Auto-resolve the client's company once on mount; null sends the advisor to name search.
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany | null>(() => {
    const companyId = resolveEninCompanyId(clientOrgId, clientCountry);
    return companyId ? { companyId, companyName: clientName, fromSearch: false } : null;
  });
  const [showPicker, setShowPicker] = useState(false);

  // Name-search state — rows: null = not searched yet, [] = searched with no hits.
  const [query, setQuery] = useState("");
  const [searchRows, setSearchRows] = useState<EninCompanySearchHit[] | null>(null);
  const [searchPending, setSearchPending] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleRun = () => {
    if (!selectedCompany) return;
    setError(null);
    startTransition(async () => {
      try {
        const input: EninLookupInput = selectedCompany.fromSearch
          ? {
              companyId: selectedCompany.companyId,
              companyName: selectedCompany.companyName ?? undefined,
            }
          : {
              orgId: clientOrgId ?? undefined,
              country: clientCountry ?? undefined,
              companyName: clientName ?? undefined,
            };
        const newSnapshot = await onRunLookup(input);
        setLocalSnapshot(newSnapshot);
        setShowPicker(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : t.unexpectedError);
      }
    });
  };

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setSearchError(null);
    setSearchPending(true);
    try {
      setSearchRows(await onSearchCompanies(trimmed));
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : t.searchFailed);
    } finally {
      setSearchPending(false);
    }
  };

  const handlePick = (hit: EninCompanySearchHit) => {
    setSelectedCompany({ companyId: hit.companyId, companyName: hit.name, fromSearch: true });
  };

  const handleRerun = () => {
    if (!localSnapshot) return;
    setError(null);
    // Confirm with the company from the saved snapshot, whatever its provenance.
    setSelectedCompany({
      companyId: localSnapshot.companyId,
      companyName: localSnapshot.companyName ?? null,
      fromSearch: true,
    });
    setShowPicker(true);
  };

  // Frozen-empty: instance completed without the lookup ever being run.
  if (disabled && !localSnapshot) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.frozenEmpty}
      </div>
    );
  }

  if (localSnapshot && !showPicker) {
    return (
      <EninLookupResultPanel
        snapshot={localSnapshot}
        onRerun={handleRerun}
        onScreenPersons={onScreenPersons ? handleScreenPersons : undefined}
        screeningPending={screeningPending}
        screeningError={screeningError}
        isPending={isPending}
        disabled={disabled}
        style={style}
        title={title}
        description={description}
        lang={lang}
      />
    );
  }

  return (
    <EninLookupPicker
      selectedCompany={selectedCompany}
      onRun={handleRun}
      onClearSelection={() => setSelectedCompany(null)}
      query={query}
      setQuery={setQuery}
      onSearch={handleSearch}
      searchRows={searchRows}
      onPick={handlePick}
      searchPending={searchPending}
      searchError={searchError}
      error={error}
      isPending={isPending}
      disabled={disabled}
      canCancel={!!localSnapshot}
      onCancel={() => setShowPicker(false)}
      style={style}
      title={title}
      description={description}
      hasValidationError={hasValidationError}
    />
  );
}
